import { FCM } from "@capacitor-community/fcm";
import { Plugins } from "@capacitor/core";
import {
	type ActionPerformed as LocalNotificationActionPerformed,
	LocalNotifications,
} from "@capacitor/local-notifications";
import {
	type ActionPerformed as PushNotificationActionPerformed,
	PushNotifications,
} from "@capacitor/push-notifications";
import * as Sentry from "@sentry/browser";

import { DebugLogger } from "~/lib/log";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { mobileVersion, type Version, versionGte, versionLt } from "~/lib/version";

import {
	commitNewToken,
	DeviceContext,
	getCollapseKey,
	type NotificationPermissionState,
	NotificationsImpl,
	openNotificationURL,
	showPermissionRequestToast,
} from "./shared";

const { FellowPlugin } = Plugins;
const log = new DebugLogger("[Notifications.ios]").log;

export default class IOSImpl extends NotificationsImpl {
	public readonly platform = DeviceContext.IOS;

	addEventListeners(): Promise<void> {
		PushNotifications.addListener("registrationError", error => {
			console.error("[Notifications.ios] Registration error", { error });
			Sentry.captureException(error);
		});

		PushNotifications.addListener("pushNotificationReceived", payload => {
			log("Received notification", { payload });
			const collapseKey = getCollapseKey(payload);
			if (collapseKey && this.idIsCleared(collapseKey)) {
				log("Key matches outerObjectContextId, not sending notification");
				return;
			}

			LocalNotifications.schedule({
				notifications: [
					{
						// always a 5 digit number
						id: ((Math.random() * (99999 - 10000) + 1) | 0) + 10000,
						title: payload.title ?? payload.data.title,
						// Passing subtitle currently does nothing until this PR is merged:
						// https://github.com/ionic-team/capacitor-plugins/pull/1097
						//
						// TODO: Remove ts-ignore once above PR is merged
						// @ts-ignore
						subtitle: payload.subtitle,
						body: payload.body ?? payload.data.body,
						// eslint-disable-next-line camelcase
						extra: { url: payload.data.url, collapse_key: collapseKey },
					},
				],
			});
		});

		function notificationHandler(payload: PushNotificationActionPerformed | LocalNotificationActionPerformed) {
			log("Notification was clicked", { payload });
			let data;
			if ("extra" in payload.notification) {
				data = payload.notification.extra;
			} else if ("data" in payload.notification) {
				data = payload.notification.data;
			}

			const url = data.url;
			if (typeof url === "string") {
				openNotificationURL(url);
			}
		}

		PushNotifications.addListener("pushNotificationActionPerformed", notificationHandler);
		LocalNotifications.addListener("localNotificationActionPerformed", notificationHandler);

		return Promise.resolve();
	}

	private async requestPlatformPermission() {
		const permission = await PushNotifications.requestPermissions();
		if (permission.receive === "granted") {
			dispatchAnalyticsTrack("Notification Permission Enabled");
		}
		this.notificationsRef.register(this);
	}

	async requestPermission({
		message = "Fellow needs your permission to enable push notifications.",
		cta = "Enable",
		skipToast = false,
	}) {
		const permission = await this.notificationPermission();

		if (permission === "default") {
			if (skipToast) {
				this.requestPlatformPermission();
				return;
			}

			showPermissionRequestToast({
				cta,
				message,
				onEnable: this.requestPlatformPermission.bind(this),
			});
		}
	}

	protected async doRegister(deviceId: string, platform: string): Promise<void> {
		const permission = await this.notificationPermission();

		if (permission === "granted") {
			await PushNotifications.register()
				.then(() => FCM.getToken())
				.then(({ token }: { token: string }) => commitNewToken(deviceId, platform, token))
				.catch((e: string) => {
					console.error("[Notifications.ios] Failed to get or commit token", e);
					Sentry.captureException(e);
				});
		} else if (permission === "default") {
			showPermissionRequestToast({
				cta: "Enable",
				message: "Fellow needs your permission to enable desktop notifications",
				onEnable: this.requestPlatformPermission.bind(this),
			});
		}
	}

	/**
	 * On iOS, once we try to register our notification handlers, the user has to
	 * choose whether to grant or deny permission. There is no "default" case
	 * unless we haven't even tried to get access, which we do immediately if the
	 * user has the setting enabled.
	 */
	async notificationPermission(): Promise<NotificationPermissionState> {
		const version: Version = await mobileVersion().catch(() => [0, 0, 0]);

		// FCM plugin is only available in version 1.1.0
		if (versionLt(version, [1, 1, 0])) {
			return "denied";
		}

		if (versionGte(version, [1, 1, 6])) {
			const { permission } = await FellowPlugin.getNotificationPermission();
			return permission;
		}

		const { value } = await LocalNotifications.areEnabled();
		return value ? "granted" : "denied";
	}

	async clearNotificationThread(id: string): Promise<void> {
		const deliveredNotifications = await PushNotifications.getDeliveredNotifications();
		const notifications = deliveredNotifications.notifications.filter(notif => getCollapseKey(notif) === id);

		if (notifications.length > 0) {
			log("Found existing notifications in thread removing them");
			PushNotifications.removeDeliveredNotifications({ notifications });
		}
	}
}
